import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { trpc } from "../../lib/trpc";
import { Drawer } from "../Drawer";
import { Icon } from "../ui/Icon";
import { SearchSelect } from "../ui/SearchSelect";
import { ConfirmDialog } from "../ui/ConfirmDialog";
import { STATUS_META, type ProjectStatus } from "./taskMeta";
import { localMidnight } from "./TaskDetailDrawer";
import "./ProjectForm.css";

/**
 * Proje oluşturma VE düzenleme.
 *
 * `projectId` verilirse düzenleme modu: künye + üye yönetimi + arşivleme +
 * (yalnız boş projede) kalıcı silme. Önce yalnız oluşturma vardı; `project.update`
 * ve `setMember` uçları yazılmış ama HİÇBİR ekrandan çağrılmıyordu — kullanıcı bir
 * projeyi açtıktan sonra adını bile değiştiremiyordu.
 */

const ROLES = ["owner", "manager", "auditor", "watcher"] as const;

interface Props {
  open: boolean;
  projectId: string | null; // null → yeni proje
  onClose: () => void;
  onCreated: (id: string) => void;
  onDeleted?: () => void;
}

function toDateInput(iso: string | Date | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function ProjectFormDrawer({ open, projectId, onClose, onCreated, onDeleted }: Props) {
  const qc = useQueryClient();
  const isEdit = !!projectId;

  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [ownerId, setOwnerId] = useState<string | null>(null);
  const [status, setStatus] = useState<ProjectStatus>("planning");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [memberRole, setMemberRole] = useState<(typeof ROLES)[number]>("watcher");
  const [delOpen, setDelOpen] = useState(false);
  const [delCode, setDelCode] = useState("");

  const usersQ = trpc.project.project.assignableUsers.useQuery(undefined, { enabled: open, staleTime: 5 * 60_000 });
  const getQ = trpc.project.project.get.useQuery({ id: projectId ?? "" }, { enabled: open && isEdit, staleTime: 3_000 });
  const p = getQ.data ?? null;

  const invalidate = () => qc.invalidateQueries();
  const createMut = trpc.project.project.create.useMutation();
  const updateMut = trpc.project.project.update.useMutation({ onSuccess: invalidate, onError: (e) => setErr(e.message) });
  const memberMut = trpc.project.project.setMember.useMutation({ onSuccess: invalidate, onError: (e) => setErr(e.message) });
  const removeMut = trpc.project.project.remove.useMutation({ onError: (e) => setErr(e.message) });

  // Açılışta / proje değişince sıfırla. Düzenlemede alanlar `p` gelince dolar.
  useEffect(() => {
    if (!open) return;
    setErr(null); setDelOpen(false); setDelCode(""); setMemberRole("watcher");
    if (!isEdit) {
      setName(""); setDesc(""); setOwnerId(null); setStatus("planning"); setStart(""); setEnd("");
    }
  }, [open, projectId, isEdit]);

  useEffect(() => {
    if (!p) return;
    setName(p.name); setDesc(p.description ?? ""); setOwnerId(p.ownerId ?? null);
    setStatus(p.status as ProjectStatus);
    setStart(toDateInput(p.startDate)); setEnd(toDateInput(p.endDate));
  }, [p]);

  const userOptions = (usersQ.data ?? []).map((u) => ({ value: u.id, label: u.name }));
  const memberIds = new Set((p?.members ?? []).map((m) => m.userId));
  const addableUsers = userOptions.filter((u) => !memberIds.has(u.value));
  const busy = createMut.isPending || updateMut.isPending;

  async function submit() {
    if (!name.trim()) { setErr("Proje adı zorunlu."); return; }
    if (start && end && end < start) { setErr("Bitiş tarihi başlangıçtan önce olamaz."); return; }
    setErr(null);
    const payload = {
      name: name.trim(),
      description: desc.trim() || null,
      ownerId: ownerId ?? null,
      startDate: start ? localMidnight(start) : null,
      endDate: end ? localMidnight(end) : null,
    };
    try {
      if (isEdit && projectId) {
        await updateMut.mutateAsync({ id: projectId, status, ...payload });
      } else {
        const res = await createMut.mutateAsync(payload);
        onCreated(res.id);
      }
      await qc.invalidateQueries();
      onClose();
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  return (
    <>
      <Drawer
        open={open} onClose={onClose} size="lg" dismissible={false}
        title={isEdit ? (p ? `${p.code} — Projeyi Düzenle` : "Projeyi Düzenle") : "Yeni Proje"}
        subtitle={isEdit ? "Künye, üyeler ve durum" : "Kod otomatik üretilir (PRJ-yıl-sıra)"}
        footer={
          <div style={{ display: "flex", gap: 8, width: "100%", alignItems: "center" }}>
            <button type="button" className="btn-md primary" onClick={submit} disabled={busy}>
              {busy ? "Kaydediliyor…" : isEdit ? "Kaydet" : "Oluştur"}
            </button>
            {isEdit ? (
              <button type="button" className="btn-md danger-outline" onClick={() => { setErr(null); setDelOpen(true); }}>
                <Icon name="trash" size={14} />Sil
              </button>
            ) : null}
            <div style={{ flex: 1 }} />
            <button type="button" className="btn-md ghost" onClick={onClose}>Vazgeç</button>
          </div>
        }
      >
        {err ? <div className="form-banner-err" style={{ marginBottom: 12 }}>{err}</div> : null}

        {isEdit && getQ.isLoading ? (
          <p className="pf-empty">Yükleniyor…</p>
        ) : (
          <div className="pf-wrap">
            <div className="form-field">
              <label className="lbl" htmlFor="pf-name">Proje Adı <span className="req">*</span></label>
              <input id="pf-name" className="form-input" value={name} onChange={(e) => setName(e.target.value)}
                placeholder="Örn. Hat 3 Modernizasyonu" autoFocus={!isEdit} />
            </div>

            <div className="form-field">
              <label className="lbl" htmlFor="pf-desc">Açıklama</label>
              <textarea id="pf-desc" className="form-textarea" rows={3} value={desc} onChange={(e) => setDesc(e.target.value)} />
            </div>

            <div className="form-row">
              <div className="form-field">
                <span className="lbl">Proje Sahibi</span>
                <SearchSelect value={ownerId} options={userOptions} onChange={setOwnerId}
                  placeholder={isEdit ? "Seç…" : "Boş bırakılırsa siz"} searchPlaceholder="Kişi ara…"
                  emptyText="Kullanıcı yok" icon="user" />
                <span className="help">Proje sahibi projedeki tüm görevleri görür.</span>
              </div>
              {isEdit ? (
                <div className="form-field">
                  <label className="lbl" htmlFor="pf-status">Durum</label>
                  <select id="pf-status" className="form-select" value={status}
                    onChange={(e) => setStatus(e.target.value as ProjectStatus)}>
                    {Object.entries(STATUS_META.project).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                  <span className="help">
                    {status === "cancelled"
                      ? "Arşivlendi: proje listede gizlenir, verileri korunur, istediğinizde geri alabilirsiniz."
                      : "Biten ya da vazgeçilen projeyi “İptal” yaparak arşivleyebilirsiniz."}
                  </span>
                </div>
              ) : null}
            </div>

            <div className="form-row">
              <div className="form-field">
                <label className="lbl" htmlFor="pf-start">Başlangıç</label>
                <input id="pf-start" className="form-input" type="date" value={start} onChange={(e) => setStart(e.target.value)} />
              </div>
              <div className="form-field">
                <label className="lbl" htmlFor="pf-end">Bitiş</label>
                <input id="pf-end" className="form-input" type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
              </div>
            </div>

            {isEdit && p ? (
              <section className="pf-sec">
                <div className="pf-sec-head">
                  <span className="pf-sec-title">Proje Üyeleri ({p.members.length})</span>
                  <div className="pf-member-add">
                    <select className="form-select" value={memberRole}
                      onChange={(e) => setMemberRole(e.target.value as (typeof ROLES)[number])} aria-label="Üye rolü">
                      {ROLES.map((r) => <option key={r} value={r}>{STATUS_META.roleLabel[r]}</option>)}
                    </select>
                    <SearchSelect value={null} options={addableUsers}
                      onChange={(v) => { if (v && projectId) { setErr(null); memberMut.mutate({ projectId, userId: v, role: memberRole }); } }}
                      placeholder="Üye ekle…" searchPlaceholder="Kişi ara…" emptyText="Eklenecek kullanıcı yok" icon="user" />
                  </div>
                </div>
                <p className="help pf-note">
                  Üyeler projedeki <b>tüm</b> görevleri görür. Yalnız belirli görevleri görmesi
                  gereken kişiyi üye yapmayın — doğrudan o göreve atayın.
                </p>
                {p.members.length === 0 ? (
                  <p className="pf-empty">Üye yok.</p>
                ) : (
                  <div className="pf-members">
                    {p.members.map((m) => (
                      <span key={m.userId} className="pf-chip">
                        <span className="pf-av">{m.name.charAt(0).toUpperCase()}</span>
                        {m.name}
                        <i className="pf-role">{STATUS_META.roleLabel[m.role as keyof typeof STATUS_META.roleLabel]}</i>
                        <button type="button" aria-label={`${m.name} üyeliğini kaldır`}
                          onClick={() => { setErr(null); memberMut.mutate({ projectId: projectId!, userId: m.userId, role: null }); }}>
                          <Icon name="x" size={11} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </section>
            ) : null}
          </div>
        )}
      </Drawer>

      <ConfirmDialog
        open={delOpen}
        title="Proje kalıcı olarak silinsin mi?"
        message={
          <>
            <p className="pf-del-warn">
              Bu işlem <b>geri alınamaz</b>. Kalıcı silme yalnız <b>hiç görevi olmayan</b> projeler
              içindir; görev varsa sunucu reddeder — o durumda projeyi <b>arşivleyin</b>
              (Durum → İptal), veriler korunur.
            </p>
            <div className="form-field">
              <label className="lbl" htmlFor="pf-delcode">Onaylamak için proje kodunu yazın: <b>{p?.code}</b></label>
              <input id="pf-delcode" className="form-input" value={delCode} onChange={(e) => setDelCode(e.target.value)}
                placeholder={p?.code ?? ""} autoComplete="off" />
            </div>
            {err ? <div className="form-banner-err pf-del-err">{err}</div> : null}
          </>
        }
        tone="danger" confirmLabel="Kalıcı Olarak Sil" cancelLabel="Vazgeç"
        busy={removeMut.isPending}
        onConfirm={() => {
          if (!projectId || delCode.trim() !== p?.code) {
            setErr(`Onay için proje kodunu birebir yazın: ${p?.code ?? ""}`);
            return;
          }
          setErr(null);
          removeMut.mutate({ id: projectId, confirmCode: delCode.trim() }, {
            onSuccess: async () => { await qc.invalidateQueries(); setDelOpen(false); onDeleted?.(); onClose(); },
          });
        }}
        onCancel={() => { setDelOpen(false); setDelCode(""); setErr(null); }}
      />
    </>
  );
}
